// src/App.js

import React from "react";
import { Toaster } from "react-hot-toast";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";

import ProtectedRoute from "./components/ProtectedRoute";
import { UserProvider } from "./context/UserContext";
import { applyStoredTheme, clearTheme } from "./utils/theme";


// Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import Dashboard from "./pages/Dashboard";
import MfaSetupPage from "./pages/MfaSetupPage";
import PersonalProfilePage from "./pages/PersonalProfilePage";

import WerkbonList from "./pages/Werkbonnen";
import WerkbonDetail from "./pages/WerkbonDetail";
import OpnameWerkbon from "./pages/OpnameWerkbon";

import AdminDashboard from "./pages/AdminDashboard";
import PlatformDashboardPage from "./pages/PlatformDashboardPage";
import RoleManagement from "./pages/RoleManagement";
import AdminTenants from "./pages/AdminTenants";
import AdminTenantOnboardingPage from "./pages/AdminTenantOnboardingPage";
import AdminTenantFirstAdminPage from "./pages/AdminTenantFirstAdminPage";
import AdminTenantMonitorPage from "./pages/AdminTenantMonitorPage";
import AdminTenantDetailPage from "./pages/AdminTenantDetailPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import BeheerAuditLog from "./pages/BeheerAuditLog";
import BeheerAuditbeleid from "./pages/BeheerAuditbeleid";
import BeheerApiFouten from "./pages/BeheerApiFouten";
import ForcePasswordResetPage from "./pages/ForcePasswordResetPage";

import TestPDF from "./pages/TestPDF";
import PinturaTest from "./components/PinturaTest";

import ProjectleiderLayout from "./pages/ProjectleiderLayout";
import PLDashboard from "./pages/pl/Dashboard";
import PlanningPage from "./pages/pl/PlanningPage";
import InzetCapaciteitPage from "./pages/pl/InzetCapaciteitPage";
import ProjectWeekOverviewPage from "./pages/pl/ProjectWeekOverviewPage";
import ProjectControlPage from "./pages/pl/ProjectControlPage";
import UrenbeheerPage from "./pages/pl/UrenbeheerPage";
import UursoortenPage from "./pages/pl/UursoortenPage";
import CalculatiesPage from "./pages/pl/CalculatiesPage";
import ProjectenPage from "./pages/pl/Projecten";
import MonteursPage from "./pages/pl/Monteurs";
import MandagstatenPage from "./pages/pl/Mandagstaten";
import OnboardingWizardsPage from "./pages/pl/OnboardingWizardsPage";
import LeaveRequestsReviewPage from "./pages/pl/LeaveRequestsReviewPage";
import PLNotificationsPage from "./pages/pl/NotificationsPage";
import PhotoOverviewPage from "./pages/pl/PhotoOverviewPage";
import KnowledgeBasePage from "./pages/pl/KnowledgeBasePage";
import StatusOverviewDemoPage from "./pages/pl/StatusOverviewDemoPage";
import KamDashboardPage from "./pages/pl/KamDashboardPage";
import KamReportsPage from "./pages/pl/KamReportsPage";
import KamReportDetailPage from "./pages/pl/KamReportDetailPage";
import KamToolboxesPage from "./pages/pl/KamToolboxesPage";
import KamAnalysePage from "./pages/pl/KamAnalysePage";
import FinanceSafetyPage from "./pages/pl/FinanceSafetyPage";
import TechnicalAssetsPage from "./pages/pl/TechnicalAssetsPage";
import SupportTicketsAdminPage from "./pages/SupportTicketsAdminPage";
import ProcurementOrdersPage from "./pages/ProcurementOrdersPage";
import ProcurementOrderDetailPage from "./pages/ProcurementOrderDetailPage";
import ProcurementProcessingPage from "./pages/ProcurementProcessingPage";
import SupplierInvoicesPage from "./pages/SupplierInvoicesPage";
import SupplierInvoiceDetailPage from "./pages/SupplierInvoiceDetailPage";
import ExternalDeploymentsPage from "./pages/ExternalDeploymentsPage";
import ExternalDeploymentDetailPage from "./pages/ExternalDeploymentDetailPage";
import NieuweWerkbon from "./pages/NieuweWerkbon";
import KlantenPage from "./pages/KlantenPage";
import KlantDetailPage from "./pages/KlantDetailPage";
import CustomersImportPage from "./pages/CustomersImportPage";
import CustomerImportHistoryPage from "./pages/CustomerImportHistoryPage";
import CustomerImportBatchDetailPage from "./pages/CustomerImportBatchDetailPage";




import MonteurLayout from "./layouts/MonteurLayout";
import MonteurWerkbonnenPage from "./pages/monteur/MonteurWerkbonnenPage";
import MonteurOpnamesPage from "./pages/monteur/MonteurOpnamesPage";
import MonteurSiteSurveyPage from "./pages/monteur/MonteurSiteSurveyPage";
import MonteurFotosPage from "./pages/monteur/MonteurFotosPage";
import MonteurNotesPage from "./pages/monteur/MonteurNotesPage";
import MonteurNotificationsPage from "./pages/monteur/MonteurNotificationsPage";
import MonteurLeaveRequestsPage from "./pages/monteur/MonteurLeaveRequestsPage";
import MonteurTemplatesPage from "./pages/monteur/MonteurTemplatesPage";
import MonteurFormRunnerPage from "./pages/monteur/MonteurFormRunnerPage";
import MonteurToolboxPage from "./pages/monteur/MonteurToolboxPage";
import MonteurDocumentenPage from "./pages/monteur/MonteurDocumentenPage";
import DisplaySettingsPage from "./pages/DisplaySettingsPage";
import TemplatesPage from "./pages/TemplatesPage";
import TemplateBuilderPage from "./pages/TemplateBuilderPage";
import InstallationsPage from "./pages/fire-logbook/InstallationsPage";
import InstallationDetailPage from "./pages/fire-logbook/InstallationDetailPage";
import Logboek24LandingPage from "./pages/Logboek24LandingPage";
import PublicLandingBySlugPage from "./pages/PublicLandingBySlugPage";
import PublicMandagstaatSignaturePage from "./pages/PublicMandagstaatSignaturePage";
import PortalDashboardPage from "./pages/portal/PortalDashboardPage";
import PortalWorkordersPage from "./pages/portal/PortalWorkordersPage";
import PortalInstallationsPage from "./pages/portal/PortalInstallationsPage";
import PortalInstallationDetailPage from "./pages/portal/PortalInstallationDetailPage";
import PortalWorkorderDetailPage from "./pages/portal/PortalWorkorderDetailPage";
import PortalSuppliersPage from "./pages/portal/PortalSuppliersPage";
import PortalProfilePage from "./pages/portal/PortalProfilePage";
import PortalRequestsPage from "./pages/portal/PortalRequestsPage";
import PortalCommunicationsPage from "./pages/portal/PortalCommunicationsPage";
import CustomerPortalRouteGate from "./components/CustomerPortalRouteGate";
import { useUser } from "./context/UserContext";
import TenantAdminAccessRoute from "./components/TenantAdminAccessRoute";
import TenantAdminHubPage from "./pages/tenant-admin/TenantAdminHubPage";
import TenantAdminPlaceholderPage from "./pages/tenant-admin/TenantAdminPlaceholderPage";
import TenantAdminWorkflowsPage from "./pages/tenant-admin/TenantAdminWorkflowsPage";
import TenantAdminWorkflowDetailPage from "./pages/tenant-admin/TenantAdminWorkflowDetailPage";
import TenantAdminUsersPage from "./pages/tenant-admin/TenantAdminUsersPage";
import TenantAdminRolesPage from "./pages/tenant-admin/TenantAdminRolesPage";
import TenantAdminSettingsPage from "./pages/tenant-admin/TenantAdminSettingsPage";
import TenantAdminModulesPage from "./pages/tenant-admin/TenantAdminModulesPage";
import TenantAdminModuleDetailPage from "./pages/tenant-admin/TenantAdminModuleDetailPage";
import TenantAdminIntegrationsPage from "./pages/tenant-admin/TenantAdminIntegrationsPage";
import TenantAdminIntegrationDetailPage from "./pages/tenant-admin/TenantAdminIntegrationDetailPage";
import TenantAdminStatusPage from "./pages/tenant-admin/TenantAdminStatusPage";
import TenantAdminBrandingDocumentsPage from "./pages/tenant-admin/TenantAdminBrandingDocumentsPage";
import TenantAdminDocumentLibraryPage from "./pages/tenant-admin/TenantAdminDocumentLibraryPage";
import { TENANT_GOVERNANCE_PERMISSION_GROUPS } from "./constants/tenantGovernancePermissions";

const TEMP_PLATFORMADMIN_TENANT_SUPPORT_ACCESS_UNTIL = new Date(
  "2026-05-08T23:59:59.999Z"
).getTime();
const allowTempPlatformAdminTenantSupportAccess =
  Date.now() <= TEMP_PLATFORMADMIN_TENANT_SUPPORT_ACCESS_UNTIL;


// --------------------------------------------------
// App
// --------------------------------------------------

function App() {
  return (
    <UserProvider>
      <Router>
        <Toaster position="top-right" />
        <ThemeScopeManager />

        <Routes>
          <Route path="/" element={<RootEntryRoute />} />
          <Route path="/pagina/:slug" element={<PublicLandingBySlugPage />} />
          <Route path="/sign/mandagstaat/:token" element={<PublicMandagstaatSignaturePage />} />

          {/* ================= LOGIN ================= */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/wachtwoord-vergeten" element={<ForgotPasswordPage />} />
          <Route path="/reset-wachtwoord" element={<ResetPasswordPage />} />
          <Route
            path="/wachtwoord-reset"
            element={
              <ProtectedRoute>
                <ForcePasswordResetPage />
              </ProtectedRoute>
            }
          />


          {/* ============ PROJECTLEIDER LAYOUT ============ */}
         <Route
  path="/pl"
  element={
    <ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}>
      <ProjectleiderLayout />
    </ProtectedRoute>
  }
>

  {/* Default PL dashboard */}
  <Route index element={<PLDashboard />} />

  {/* 🔥 NIEUW */}
  <Route path="werkbon/nieuw" element={<NieuweWerkbon />} />
  <Route path="planning" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin"]}><PlanningPage /></ProtectedRoute>} />
  <Route path="inzet-capaciteit" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><InzetCapaciteitPage /></ProtectedRoute>} />
  <Route path="projectweekoverzicht" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin"]}><ProjectWeekOverviewPage /></ProtectedRoute>} />
  <Route path="projectcontrol" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><ProjectControlPage /></ProtectedRoute>} />
  <Route path="uren" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><UrenbeheerPage /></ProtectedRoute>} />
  <Route path="uursoorten" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin"]}><UursoortenPage /></ProtectedRoute>} />
  <Route path="calculaties" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin"]}><CalculatiesPage /></ProtectedRoute>} />
  <Route path="projecten" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><ProjectenPage /></ProtectedRoute>} />
  <Route path="assets" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><TechnicalAssetsPage /></ProtectedRoute>} />
  <Route path="monteurs" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin"]}><MonteursPage /></ProtectedRoute>} />
  <Route path="mandagstaten" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin"]}><MandagstatenPage /></ProtectedRoute>} />
  <Route path="tenant-admin" element={<Navigate to="/tenant-admin" replace />} />
  <Route path="onboarding" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin"]}><OnboardingWizardsPage /></ProtectedRoute>} />
  <Route path="verlof" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><LeaveRequestsReviewPage /></ProtectedRoute>} />
  <Route path="meldingen" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><PLNotificationsPage /></ProtectedRoute>} />
  <Route path="fotos" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><PhotoOverviewPage /></ProtectedRoute>} />
  <Route path="kennisbank" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><KnowledgeBasePage /></ProtectedRoute>} />
  <Route path="kam" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller", "KAM"]}><KamDashboardPage /></ProtectedRoute>} />
  <Route path="kam/analyse" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller", "KAM"]}><KamAnalysePage /></ProtectedRoute>} />
  <Route path="kam/meldingen" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller", "KAM"]}><KamReportsPage /></ProtectedRoute>} />
  <Route path="kam/meldingen/:id" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller", "KAM"]}><KamReportDetailPage /></ProtectedRoute>} />
  <Route path="kam/toolboxen" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller", "KAM"]}><KamToolboxesPage /></ProtectedRoute>} />
  <Route path="finance-safety" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><FinanceSafetyPage /></ProtectedRoute>} />
  <Route path="status-overview-demo" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><StatusOverviewDemoPage /></ProtectedRoute>} />
  <Route path="support-tickets" element={<ProtectedRoute allowedRoles={allowTempPlatformAdminTenantSupportAccess ? ["Projectleider", "TenantAdmin", "PlatformAdmin"] : ["Projectleider", "TenantAdmin"]}><SupportTicketsAdminPage mode="tenant" /></ProtectedRoute>} />
  <Route path="weergave" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><DisplaySettingsPage /></ProtectedRoute>} />
  <Route path="inkoop" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><ProcurementOrdersPage /></ProtectedRoute>} />
  <Route path="inkoop/verwerking" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><ProcurementProcessingPage /></ProtectedRoute>} />
  <Route path="inkoop/facturen" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><SupplierInvoicesPage /></ProtectedRoute>} />
  <Route path="inkoop/facturen/:id" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><SupplierInvoiceDetailPage /></ProtectedRoute>} />
  <Route path="inkoop/:id" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><ProcurementOrderDetailPage /></ProtectedRoute>} />
  <Route path="externe-inzet" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><ExternalDeploymentsPage /></ProtectedRoute>} />
  <Route path="externe-inzet/:id" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Controller"]}><ExternalDeploymentDetailPage /></ProtectedRoute>} />
  <Route path="installaties" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Inspecteur"]}><InstallationsPage /></ProtectedRoute>} />
  <Route path="installaties/:id" element={<ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Inspecteur"]}><InstallationDetailPage /></ProtectedRoute>} />
  <Route path="brandveiligheid" element={<Navigate to="/pl/installaties" replace />} />
  <Route path="brandveiligheid/installaties/:id" element={<LegacyInstallationRedirect />} />

</Route>

          <Route
            path="/tenant-admin"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin"]}>
                <ProjectleiderLayout />
              </ProtectedRoute>
            }
          >
            <Route
              index
              element={
                <TenantAdminAccessRoute>
                  <TenantAdminHubPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="bedrijfsinrichting"
              element={
                <TenantAdminAccessRoute>
                  <Navigate to="/tenant-admin" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="rollen-rechten"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.rolesManage}
                >
                  <Navigate to="/tenant-admin/roles" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="werkprocessen"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.workflowsView}
                >
                  <Navigate to="/tenant-admin/workflows" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="monitoring"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.statusView}
                >
                  <Navigate to="/tenant-admin/status" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="users"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.usersManage}
                >
                  <TenantAdminUsersPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="roles"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.rolesManage}
                >
                  <TenantAdminRolesPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="settings"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.settingsView}
                >
                  <TenantAdminSettingsPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="bedrijfsinrichting/huisstijl-documenten"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.companyDetailsView}
                >
                  <TenantAdminBrandingDocumentsPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="bedrijfsinrichting/huistijl-documenten"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.companyDetailsView}
                >
                  <Navigate to="/tenant-admin/bedrijfsinrichting/huisstijl-documenten" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="huisstijl-documenten"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.companyDetailsView}
                >
                  <TenantAdminBrandingDocumentsPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="documenten"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.documentsView}
                >
                  <TenantAdminDocumentLibraryPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="bedrijfsinrichting/documenten"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.documentsView}
                >
                  <TenantAdminDocumentLibraryPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="huistijl-documenten"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.companyDetailsView}
                >
                  <Navigate to="/tenant-admin/huisstijl-documenten" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="status"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.statusView}
                >
                  <TenantAdminStatusPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="modules"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.modulesView}
                >
                  <TenantAdminModulesPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="modules/:moduleKey"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.modulesView}
                >
                  <TenantAdminModuleDetailPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="workflows"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.workflowsView}
                >
                  <TenantAdminWorkflowsPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="workflows/:workflowKey"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.workflowsView}
                >
                  <TenantAdminWorkflowDetailPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="workflows/customer-portal"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.workflowsView}
                >
                  <Navigate to="/tenant-admin/workflows/klantportaal" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="integrations"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView}
                >
                  <TenantAdminIntegrationsPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="integrations/:integrationKey"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView}
                >
                  <TenantAdminIntegrationDetailPage />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="integrations/mail"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView}
                >
                  <Navigate to="/tenant-admin/integrations/mailinstellingen" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="integrations/branding"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView}
                >
                  <Navigate to="/tenant-admin/integrations/huisstijl-branding" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="pdf-templates"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView}
                >
                  <Navigate to="/tenant-admin/integrations/pdf-templates" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="branding"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.integrationsView}
                >
                  <Navigate to="/tenant-admin/integrations/huisstijl-branding" replace />
                </TenantAdminAccessRoute>
              }
            />
            <Route
              path="customer-portal"
              element={
                <TenantAdminAccessRoute
                  requiredAnyPermissions={TENANT_GOVERNANCE_PERMISSION_GROUPS.workflowsView}
                >
                  <TenantAdminPlaceholderPage
                    title="Klantportaal workflow"
                    description="Beheer publicatie- en zichtbaarheidregels voor klantportaalprocessen."
                  />
                </TenantAdminAccessRoute>
              }
            />
          </Route>

          <Route
            path="/i/:qrToken"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Inspecteur"]}>
                <InstallationDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/brandveiligheid/installaties/:id"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin", "Inspecteur"]}>
                <LegacyGlobalInstallationRedirect />
              </ProtectedRoute>
            }
          />

          <Route
            path="/dashboard/mandagstaten"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin", "Projectleider", "TenantAdmin"]}>
                <MandagstatenPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/templates"
            element={
              <ProtectedRoute allowedRoles={["Projectleider", "TenantAdmin", "PlatformAdmin", "Inspecteur", "Monteur"]}>
                <TemplatesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/template-builder/:id"
            element={
              <ProtectedRoute allowedRoles={["Projectleider", "TenantAdmin", "PlatformAdmin", "Inspecteur", "Monteur"]}>
                <TemplateBuilderPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/forms/:templateId/run"
            element={
              <ProtectedRoute allowedRoles={["Monteur", "Projectleider", "TenantAdmin", "PlatformAdmin"]}>
                <MonteurFormRunnerPage />
              </ProtectedRoute>
            }
          />



          {/* ================= DASHBOARD ================= */}
          <Route
            path="/mfa/setup"
            element={
              <ProtectedRoute>
                <MfaSetupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profiel"
            element={
              <ProtectedRoute>
                <PersonalProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute
                allowedRoles={[
                  "PlatformAdmin",
                  "Projectleider",
                  "Projectmanager",
                  "Werkvoorbereider",
                  "Demo"
                ]}
              >
                <Dashboard />
              </ProtectedRoute>
            }
          />


          {/* ================= WERKBONNEN ================= */}
          <Route
            path="/werkbon"
            element={
              <ProtectedRoute>
                <WerkbonList />
              </ProtectedRoute>
            }
          />

          <Route
            path="/werkbon/nieuw"
            element={
              <ProtectedRoute>
                <NieuweWerkbon />
              </ProtectedRoute>
            }
          />

          <Route
            path="/werkbon/:id"
            element={
              <ProtectedRoute>
                <WorkorderDetailEntryRoute />
              </ProtectedRoute>
            }
          />


          {/* ================= OPNAME ================= */}
          <Route
            path="/opname"
            element={
              <ProtectedRoute>
                <OpnameWerkbon />
              </ProtectedRoute>
            }
          />


          {/* ================= ADMIN ================= */}
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin"]}>
                <PlatformDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/platformbeheer"
            element={<Navigate to="/admin/dashboard" replace />}
          />
          <Route
            path="/admin/monitoring"
            element={<Navigate to="/admin/operations" replace />}
          />
          <Route path="/admin/operations" element={<ProtectedRoute allowedRoles={["PlatformAdmin"]}><AdminDashboard section="operations" /></ProtectedRoute>} />
          <Route path="/admin/security" element={<ProtectedRoute allowedRoles={["PlatformAdmin"]}><AdminDashboard section="security" /></ProtectedRoute>} />
          <Route path="/admin/integrations" element={<ProtectedRoute allowedRoles={["PlatformAdmin"]}><AdminDashboard section="integrations" /></ProtectedRoute>} />

          <Route
            path="/admin/rollen"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin"]}>
                <RoleManagement />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/tenants"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin"]}>
                <AdminTenants />
              </ProtectedRoute>
            }
          />
          <Route path="/admin/users" element={<ProtectedRoute allowedRoles={["PlatformAdmin"]}><AdminUsersPage /></ProtectedRoute>} />
          <Route
            path="/admin/tenants/new"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin"]}>
                <AdminTenantOnboardingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tenants/:tenantId"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin"]}>
                <AdminTenantDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/tenants/:tenantId/first-admin"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin"]}>
                <AdminTenantFirstAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tenants/:tenantId/monitor"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin"]}>
                <AdminTenantMonitorPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/auditlog"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin", "TenantAdmin", "Projectleider"]}>
                <BeheerAuditLog />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/auditbeleid"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin", "TenantAdmin", "Projectleider"]}>
                <BeheerAuditbeleid />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/api-fouten"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin", "TenantAdmin", "Projectleider"]}>
                <BeheerApiFouten />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/support-tickets"
            element={
              <ProtectedRoute allowedRoles={["PlatformAdmin"]}>
                <SupportTicketsAdminPage mode="admin" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/landing-cms"
            element={<Navigate to="/admin/dashboard" replace />}
          />


          {/* ================= MONTEUR ================= */}
          <Route
            path="/monteur"
            element={
              <ProtectedRoute
                allowedRoles={[
                  "Monteur"
                ]}
              >
                <MonteurLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/monteur/werkbonnen" replace />} />
            <Route path="dashboard" element={<Navigate to="/monteur/werkbonnen" replace />} />
            <Route path="werkbonnen" element={<MonteurWerkbonnenPage />} />
            <Route path="werkbonnen/:werkbonId" element={<MonteurWerkbonnenPage />} />
            <Route path="werkbon/nieuw" element={<NieuweWerkbon />} />

            {/* Backward-compatible monteur links */}
            <Route path="werkbon" element={<Navigate to="/monteur/werkbonnen" replace />} />
            <Route path="werkbon/:werkbonId" element={<MonteurLegacyWerkbonRedirect />} />

            <Route path="opnames" element={<MonteurOpnamesPage />} />
            <Route path="site-survey" element={<MonteurSiteSurveyPage />} />
            <Route path="fotos" element={<MonteurFotosPage />} />
            <Route path="templates" element={<MonteurTemplatesPage />} />
            <Route path="meldingen" element={<MonteurNotificationsPage />} />
            <Route path="verlof" element={<MonteurLeaveRequestsPage />} />
            <Route path="forms/:templateId/run" element={<MonteurFormRunnerPage />} />
            <Route path="notities" element={<MonteurNotesPage />} />
            <Route path="toolbox" element={<MonteurToolboxPage />} />
            <Route path="documenten" element={<MonteurDocumentenPage />} />
            <Route path="weergave" element={<DisplaySettingsPage />} />
          </Route>



          {/* ================= TEST ================= */}
          <Route path="/test-pdf" element={<TestPDF />} />
          <Route path="/pintura" element={<PinturaTest />} />

          {/* ================= KLANTEN ================= */}
          <Route path="/klanten" element={<KlantenPage />} />
          <Route path="/klanten/import" element={<CustomersImportPage />} />
          <Route path="/klanten/import/historie" element={<CustomerImportHistoryPage />} />
          <Route path="/klanten/import/historie/:id" element={<CustomerImportBatchDetailPage />} />
          <Route path="/klanten/:id/assets" element={<KlantDetailPage initialTab="assets" />} />
          <Route path="/klanten/:id" element={<KlantDetailPage />} />

          {/* ================= KLANTHUB ================= */}
          <Route
            path="/portaal"
            element={
              <ProtectedRoute>
                <CustomerPortalRouteGate>
                  <PortalDashboardPage />
                </CustomerPortalRouteGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portaal/werkbonnen"
            element={
              <ProtectedRoute>
                <CustomerPortalRouteGate>
                  <PortalWorkordersPage />
                </CustomerPortalRouteGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portaal/werkbonnen/:id"
            element={
              <ProtectedRoute>
                <CustomerPortalRouteGate>
                  <PortalWorkorderDetailRedirect />
                </CustomerPortalRouteGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portaal/installaties"
            element={
              <ProtectedRoute>
                <CustomerPortalRouteGate>
                  <PortalInstallationsPage />
                </CustomerPortalRouteGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portaal/leveranciers"
            element={
              <ProtectedRoute>
                <CustomerPortalRouteGate>
                  <PortalSuppliersPage />
                </CustomerPortalRouteGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portaal/berichten"
            element={
              <ProtectedRoute>
                <CustomerPortalRouteGate>
                  <PortalCommunicationsPage />
                </CustomerPortalRouteGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portaal/aanvragen"
            element={
              <ProtectedRoute>
                <CustomerPortalRouteGate>
                  <PortalRequestsPage />
                </CustomerPortalRouteGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portaal/profiel"
            element={
              <ProtectedRoute>
                <CustomerPortalRouteGate>
                  <PortalProfilePage />
                </CustomerPortalRouteGate>
              </ProtectedRoute>
            }
          />
          <Route
            path="/portaal/installaties/:id"
            element={
              <ProtectedRoute>
                <CustomerPortalRouteGate>
                  <PortalInstallationDetailPage />
                </CustomerPortalRouteGate>
              </ProtectedRoute>
            }
          />
          <Route path="/opdrachtgeversportaal" element={<Navigate to="/portaal" replace />} />
          <Route
            path="/opdrachtgeversportaal/werkbonnen"
            element={<Navigate to="/portaal/werkbonnen" replace />}
          />
          <Route
            path="/opdrachtgeversportaal/installaties"
            element={<Navigate to="/portaal/installaties" replace />}
          />
          <Route
            path="/opdrachtgeversportaal/leveranciers"
            element={<Navigate to="/portaal/leveranciers" replace />}
          />
          <Route
            path="/portaal/communicatie"
            element={<Navigate to="/portaal/berichten" replace />}
          />
          <Route
            path="/opdrachtgeversportaal/aanvragen"
            element={<Navigate to="/portaal/aanvragen" replace />}
          />
          <Route
            path="/opdrachtgeversportaal/berichten"
            element={<Navigate to="/portaal/berichten" replace />}
          />
          <Route
            path="/opdrachtgeversportaal/communicatie"
            element={<Navigate to="/portaal/berichten" replace />}
          />
          <Route
            path="/opdrachtgeversportaal/profiel"
            element={<Navigate to="/portaal/profiel" replace />}
          />
          <Route
            path="/opdrachtgeversportaal/installaties/:id"
            element={<OpdrachtgeversportaalInstallationRedirect />}
          />

          {/* ================= FALLBACK ================= */}
          <Route path="*" element={<Navigate to="/login" replace />} />


        </Routes>

      </Router>
    </UserProvider>
  );
}

export default App;

function RootEntryRoute() {
  const host = String(window.location.hostname || "").toLowerCase();
  const isAppHost = host === "app.logboek24.nl" || host.startsWith("app.");

  if (isAppHost) {
    return <Navigate to="/login" replace />;
  }

  return <Logboek24LandingPage />;
}

function MonteurLegacyWerkbonRedirect() {
  const { werkbonId } = useParams();
  if (!werkbonId) return <Navigate to="/monteur/werkbonnen" replace />;
  return <Navigate to={`/monteur/werkbonnen/${werkbonId}`} replace />;
}

function WorkorderDetailEntryRoute() {
  const { user } = useUser();
  const { id } = useParams();
  const roleNames = [
    String(user?.rol || ""),
    String(user?.role || ""),
    ...(Array.isArray(user?.roles) ? user.roles.map((item) => String(item || "")) : []),
  ]
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  const isMonteur = roleNames.includes("monteur");

  if (isMonteur && id) {
    return <Navigate to={`/monteur/werkbonnen/${id}`} replace />;
  }
  return <WerkbonDetail />;
}

function LegacyInstallationRedirect() {
  const { id } = useParams();
  if (!id) return <Navigate to="/pl/installaties" replace />;
  return <Navigate to={`/pl/installaties/${id}`} replace />;
}

function LegacyGlobalInstallationRedirect() {
  const { id } = useParams();
  if (!id) return <Navigate to="/pl/installaties" replace />;
  return <Navigate to={`/pl/installaties/${id}`} replace />;
}

function PortalWorkorderDetailRedirect() {
  const { user } = useUser();
  const { id } = useParams();
  if (!id) return <Navigate to="/portaal/werkbonnen" replace />;
  const accountType = String(user?.accountType || "internal").trim().toLowerCase();
  if (accountType === "customer_portal") {
    return <PortalWorkorderDetailPage />;
  }
  return <Navigate to={`/werkbon/${id}`} replace />;
}

function OpdrachtgeversportaalInstallationRedirect() {
  const { id } = useParams();
  if (!id) return <Navigate to="/portaal/installaties" replace />;
  return <Navigate to={`/portaal/installaties/${id}`} replace />;
}

function ThemeScopeManager() {
  const location = useLocation();

  React.useEffect(() => {
    const pathname = String(location.pathname || "");
    const isScopedRoute =
      pathname.startsWith("/monteur") ||
      pathname.startsWith("/pl") ||
      pathname.startsWith("/werkbon");

    if (isScopedRoute) {
      applyStoredTheme();
      return;
    }

    clearTheme();
  }, [location.pathname]);

  return null;
}
